/**
 * 69.98 (1), the flash materials kept (DECISIONS 319; lane H's 69.94 sweep): the hit flashes, the muzzle flashes and the
 * rockets' puffs each fade their own material's opacity, so each needs a material of its own — but disposing it when the
 * flash ended let three.js release the shader program once the last flash using it was gone, and the next flash compiled
 * it again (52 % of frames with aircraft up: a gunship's salvo flashes in bursts). A flash takes its material from here and
 * gives it back: the materials are kept, so their programs are never released. Two kinds, by what reaches the program —
 * plain (fogged, normal blending: the hit flashes, the smoke) and additive (unfogged: the muzzle's core and flame).
 * Presentation only: the same material settings as before.
 */
import * as THREE from 'three';

export type FlashKind = 'plain' | 'additive';

const free: Record<FlashKind, THREE.MeshBasicMaterial[]> = { plain: [], additive: [] };
/** Past this many waiting in a kind, a given-back material is disposed (others of its kind are in use: its program stays). */
const KEEP = 256;
const owned = new WeakMap<THREE.Material, FlashKind>();

function make(kind: FlashKind): THREE.MeshBasicMaterial {
  const m = kind === 'additive'
    ? new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, fog: false })
    : new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false });
  owned.set(m, kind);
  return m;
}

/** A flash's material: `color` at `opacity` (the flash fades it), of `kind`. Give it back with `releaseFlashMaterial`. */
export function flashMaterial(color: THREE.ColorRepresentation, opacity: number, kind: FlashKind = 'plain'): THREE.MeshBasicMaterial {
  const m = free[kind].pop() ?? make(kind);
  m.color.set(color);
  m.opacity = opacity;
  return m;
}

/** A flash ended: its material back to wait for the next (one not taken here is disposed, as before). */
export function releaseFlashMaterial(m: THREE.Material): void {
  const kind = owned.get(m);
  if (!kind || free[kind].length >= KEEP) { m.dispose(); return; }
  if (!free[kind].includes(m as THREE.MeshBasicMaterial)) free[kind].push(m as THREE.MeshBasicMaterial);
}

